import React, { useState, useRef, useEffect, useMemo, useCallback, memo } from 'react';
import { API_BASE_URL } from '../../config/backendConfig';
import { Box, IconButton, Typography, Paper, TextField, Button, Collapse, Avatar, CircularProgress, Alert, Chip, Radio, RadioGroup, FormControlLabel, FormControl, FormLabel, Autocomplete, Dialog, DialogContent, DialogTitle, Badge } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Send as SendIcon, FlightTakeoff as FlightIcon, Train as TrainIcon, Hotel as HotelIcon, LocationOn as LocationIcon, AttachMoney as MoneyIcon, WbSunny as WeatherIcon, TrendingUp as TrendingIcon, DeleteOutline as DeleteIcon, CalendarToday as CalendarIcon, AutoAwesome, People as PeopleIcon, Style as StyleIcon, Verified as VerifiedIcon } from '@mui/icons-material';
import { chatbotService, fallbackResponses, clearChatbotSession } from '../../services/chatbotApi';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// Plan Style options
const PLAN_STYLES = [
  { id: 'relaxed', label: 'Relaxed', icon: '😌' },
  { id: 'family', label: 'Family-friendly', icon: '👨‍👩‍👧' },
  { id: 'elderly', label: 'Elderly-friendly', icon: '👴' },
  { id: 'budget', label: 'Budget', icon: '💰' },
  { id: 'romantic', label: 'Romantic', icon: '💕' },
  { id: 'pilgrimage', label: 'Pilgrimage', icon: '🙏' },
  { id: 'food', label: 'Food-focused', icon: '🍜' },
  { id: 'luxury', label: 'Luxury', icon: '✨' },
  { id: 'weekend', label: 'Weekend', icon: '📅' },
];

// Trip Brief Card Component
const TripBriefCard = memo(({ brief, onClose }) => {
  if (!brief) return null;
  
  return (
    <Paper sx={{ 
      p: 1.5, 
      mb: 1.5,
      bgcolor: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
      borderRadius: '12px',
      border: '1px solid #dee2e6',
      position: 'relative'
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ 
          fontWeight: 600, 
          color: '#ab2645',
          fontSize: '0.75rem',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5
        }}>
          <StyleIcon sx={{ fontSize: '14px' }} />
          Your Trip Brief
        </Typography>
        {onClose && (
          <IconButton size="small" onClick={onClose} sx={{ p: 0.25 }}>
            <CloseIcon sx={{ fontSize: '14px' }} />
          </IconButton>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {brief.destination && (
          <Chip 
            icon={<LocationIcon sx={{ fontSize: '12px !important' }} />}
            label={brief.destination} 
            size="small" 
            sx={{ 
              bgcolor: '#ab2645', 
              color: 'white',
              fontSize: '0.65rem',
              height: '22px',
              '& .MuiChip-icon': { color: 'white' }
            }} 
          />
        )}
        {brief.dates && (
          <Chip 
            icon={<CalendarIcon sx={{ fontSize: '12px !important' }} />}
            label={brief.dates} 
            size="small" 
            sx={{ 
              bgcolor: '#495057', 
              color: 'white',
              fontSize: '0.65rem',
              height: '22px',
              '& .MuiChip-icon': { color: 'white' }
            }} 
          />
        )}
        {brief.travellers && (
          <Chip 
            icon={<PeopleIcon sx={{ fontSize: '12px !important' }} />}
            label={brief.travellers} 
            size="small" 
            sx={{ 
              bgcolor: '#6c757d', 
              color: 'white',
              fontSize: '0.65rem',
              height: '22px',
              '& .MuiChip-icon': { color: 'white' }
            }} 
          />
        )}
        {brief.trip_style && (
          <Chip 
            label={brief.trip_style} 
            size="small" 
            sx={{ 
              bgcolor: '#e9ecef', 
              color: '#495057',
              fontSize: '0.65rem',
              height: '22px',
              fontWeight: 500
            }} 
          />
        )}
      </Box>
      {brief.next_best_action && (
        <Typography variant="caption" sx={{ 
          display: 'block', 
          mt: 1, 
          color: '#6c757d',
          fontSize: '0.65rem'
        }}>
          Next: {brief.next_best_action}
        </Typography>
      )}
    </Paper>
  );
});

// Yovo Guide Badge Component
const YovoGuideBadge = memo(() => (
  <Box sx={{ 
    display: 'inline-flex', 
    alignItems: 'center', 
    gap: 0.3,
    bgcolor: '#e8f5e9',
    color: '#2e7d32',
    px: 0.75,
    py: 0.25,
    borderRadius: '4px',
    fontSize: '0.6rem',
    fontWeight: 600,
    mb: 0.5
  }}>
    <VerifiedIcon sx={{ fontSize: '10px' }} />
    Yovo Guide
  </Box>
));

// Plan Style Chips Component
const PlanStyleChips = memo(({ onSelect, selectedStyle }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
    {PLAN_STYLES.map((style) => (
      <Chip
        key={style.id}
        label={`${style.icon} ${style.label}`}
        size="small"
        onClick={() => onSelect(style)}
        sx={{
          bgcolor: selectedStyle?.id === style.id ? '#ab2645' : '#f5f5f5',
          color: selectedStyle?.id === style.id ? 'white' : '#333',
          fontSize: '0.65rem',
          height: '24px',
          cursor: 'pointer',
          '&:hover': {
            bgcolor: selectedStyle?.id === style.id ? '#8a1f38' : '#e0e0e0',
          }
        }}
      />
    ))}
  </Box>
));

// Refinement Buttons Component
const RefinementButtons = memo(({ refinements, onSelect }) => {
  if (!refinements || refinements.length === 0) return null;
  
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
      {refinements.map((ref) => (
        <Button
          key={ref.id}
          size="small"
          variant="outlined"
          onClick={() => onSelect(ref)}
          sx={{
            fontSize: '0.65rem',
            textTransform: 'none',
            borderColor: '#ab2645',
            color: '#ab2645',
            borderRadius: '16px',
            px: 1.5,
            py: 0.25,
            minHeight: '24px',
            '&:hover': {
              bgcolor: 'rgba(171, 38, 69, 0.08)',
              borderColor: '#8a1f38',
            }
          }}
        >
          {ref.label}
        </Button>
      ))}
    </Box>
  );
});

// Animated Plane Component
const AnimatedPlane = ({ isVisible, onComplete }) => {
  const [animationPhase, setAnimationPhase] = useState(0);

  useEffect(() => {
    if (isVisible) {
      // Reset animation phase when becoming visible
      setAnimationPhase(0);
      
      const timer = setTimeout(() => {
        setAnimationPhase(1); // Plane draws and moves up to center
        setTimeout(() => {
          setAnimationPhase(2); // Plane reaches center and glows
          setTimeout(() => {
            setAnimationPhase(3); // Plane moves forward (left) continuously
            setTimeout(() => {
              setAnimationPhase(4); // Plane fades out with smooth transition
              setTimeout(() => {
                onComplete && onComplete();
              }, 800); // Increased fade time for smoother transition
            }, 1200); // Increased forward movement time
          }, 600); // Increased glow time
        }, 1200); // Increased drawing time
      }, 400); // Increased initial delay
      return () => clearTimeout(timer);
    } else {
      // Reset animation phase when not visible
      setAnimationPhase(0);
    }
  }, [isVisible, onComplete]);

  if (!isVisible) return null;

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        pointerEvents: 'none',
        background: 'linear-gradient(135deg, rgba(0,0,0,0.95) 0%, rgba(0,0,0,1) 100%)',
        borderRadius: '16px',
        overflow: 'hidden',
      }}
    >
      {/* Animated Background Elements */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 20%, rgba(171, 38, 69, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.05) 0%, transparent 50%),
            linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.02) 50%, transparent 70%)
          `,
          animation: 'backgroundShimmer 3s ease-in-out infinite',
          '@keyframes backgroundShimmer': {
            '0%': { transform: 'translateX(-100%)' },
            '100%': { transform: 'translateX(100%)' }
          }
        }}
      />

      {/* Floating Particles */}
      {[...Array(8)].map((_, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            width: '3px',
            height: '3px',
            background: 'rgba(255, 255, 255, 0.6)',
            borderRadius: '50%',
            top: `${20 + i * 10}%`,
            left: `${10 + i * 12}%`,
            animation: `particleFloat${i} ${2 + i * 0.3}s ease-in-out infinite`,
            [`@keyframes particleFloat${i}`]: {
              '0%, 100%': { 
                transform: 'translateY(0px) translateX(0px) scale(1)', 
                opacity: 0.3 
              },
              '50%': { 
                transform: `translateY(-${15 + i * 2}px) translateX(${5 + i}px) scale(1.2)`, 
                opacity: 0.8 
              }
            }
          }}
        />
      ))}

      {/* Main Plane Animation Container */}
      <Box
        sx={{
          position: 'relative',
          width: '100px',
          height: '100px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transform: animationPhase === 0 ? 'translateY(0px) translateX(0px) scale(0.3)' : 
                    animationPhase === 1 ? 'translateY(0px) translateX(0px) scale(1)' :
                    animationPhase === 2 ? 'translateY(0px) translateX(-20px) scale(1)' :
                    animationPhase === 3 ? 'translateY(0px) translateX(-100px) scale(1)' :
                    'translateY(0px) translateX(-200px) scale(1)',
          opacity: animationPhase === 0 ? 0 : 
                  animationPhase === 1 ? 1 :
                  animationPhase === 2 ? 1 : 
                  animationPhase === 3 ? 1 : 0,
          transition: animationPhase === 4 ? 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          filter: animationPhase === 2 ? 'drop-shadow(0 0 15px rgba(171, 38, 69, 0.6))' : 'none',
        }}
      >
        {/* Plane SVG */}
        <svg
          width="70"
          height="50"
          viewBox="0 0 80 60"
          style={{
            transform: 'rotate(0deg)',
            transition: 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          }}
        >
          {/* Plane Body */}
          <path
            d="M15 30 L65 30 L70 35 L65 40 L15 40 Z"
            fill="none"
            stroke="#ffffff"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              strokeDasharray: '200',
              strokeDashoffset: animationPhase === 0 ? '200' : '0',
              transition: 'stroke-dashoffset 1.5s ease-in-out',
            }}
          />
          
          {/* Plane Wings */}
          <path
            d="M25 30 L35 20 L45 20 L35 30 Z"
            fill="none"
            stroke="#ffffff"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              strokeDasharray: '100',
              strokeDashoffset: animationPhase === 0 ? '100' : '0',
              transition: 'stroke-dashoffset 1.2s ease-in-out 0.3s',
            }}
          />
          
          <path
            d="M25 40 L35 50 L45 50 L35 40 Z"
            fill="none"
            stroke="#ffffff"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              strokeDasharray: '100',
              strokeDashoffset: animationPhase === 0 ? '100' : '0',
              transition: 'stroke-dashoffset 1.2s ease-in-out 0.3s',
            }}
          />
          
          {/* Plane Nose */}
          <path
            d="M15 30 L5 35 L15 40 Z"
            fill="none"
            stroke="#ffffff"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              strokeDasharray: '50',
              strokeDashoffset: animationPhase === 0 ? '50' : '0',
              transition: 'stroke-dashoffset 1s ease-in-out 0.6s',
            }}
          />
          
          {/* Plane Tail */}
          <path
            d="M65 30 L75 25 L75 35 L65 40 Z"
            fill="none"
            stroke="#ffffff"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              strokeDasharray: '60',
              strokeDashoffset: animationPhase === 0 ? '60' : '0',
              transition: 'stroke-dashoffset 1s ease-in-out 0.9s',
            }}
          />
          
          {/* Propeller */}
          <circle
            cx="15"
            cy="35"
            r="3"
            fill="none"
            stroke="#ab2645"
            strokeWidth="1.5"
            style={{
              strokeDasharray: '20',
              strokeDashoffset: animationPhase === 0 ? '20' : '0',
              transition: 'stroke-dashoffset 0.8s ease-in-out 1.2s',
            }}
          />
          
          {/* Propeller Blades */}
          <path
            d="M15 35 L20 35 M15 35 L10 35 M15 35 L15 30 M15 35 L15 40"
            stroke="#ab2645"
            strokeWidth="1"
            strokeLinecap="round"
            style={{
              strokeDasharray: '40',
              strokeDashoffset: animationPhase === 0 ? '40' : '0',
              transition: 'stroke-dashoffset 0.8s ease-in-out 1.2s',
              animation: animationPhase >= 1 ? 'propellerSpin 0.1s linear infinite' : 'none',
            }}
          />
        </svg>


        {/* Speed Trail Effect - Only shows during forward movement */}
        {animationPhase >= 3 && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '120px',
              height: '4px',
              background: 'linear-gradient(90deg, transparent 0%, rgba(171, 38, 69, 0.6) 30%, rgba(255, 255, 255, 0.4) 70%, transparent 100%)',
              borderRadius: '2px',
              animation: 'speedTrail 0.5s ease-out infinite',
              '@keyframes speedTrail': {
                '0%': { 
                  transform: 'translate(-50%, -50%) scaleX(0)',
                  opacity: 0 
                },
                '50%': { 
                  transform: 'translate(-50%, -50%) scaleX(1)',
                  opacity: 0.8 
                },
                '100%': { 
                  transform: 'translate(-50%, -50%) scaleX(1.2)',
                  opacity: 0 
                }
              }
            }}
          />
        )}

        {/* Propeller Spin Animation */}
        <style>
          {`
            @keyframes propellerSpin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}
        </style>
      </Box>

    </Box>
  );
};

const Chatbot = memo(({ open, setOpen, flightData = null, flushBottom = false }) => {
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [conversationHistory, setConversationHistory] = useState([]);
  const messagesEndRef = useRef(null);
  const handleSendRef = useRef(null);
  
  // Trip planning state
  const [currentTripBrief, setCurrentTripBrief] = useState(null);
  const [selectedPlanStyle, setSelectedPlanStyle] = useState(null);
  
  // Off-topic tracking for intent classification
  const [offTopicCount, setOffTopicCount] = useState(0);
  
  // Opening animation state
  // const [showOpeningAnimation, setShowOpeningAnimation] = useState(false);
  // const [animationComplete, setAnimationComplete] = useState(false);

  // Interactive form states
  const [showInteractiveForm, setShowInteractiveForm] = useState(false);
  const [interactiveFormType, setInteractiveFormType] = useState('');
  const [tripType, setTripType] = useState('oneway');
  const [departureDate, setDepartureDate] = useState(null);
  const [returnDate, setReturnDate] = useState(null);
  const [departureCity, setDepartureCity] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [departureCityOptions, setDepartureCityOptions] = useState([]);
  const [departureCityLoading, setDepartureCityLoading] = useState(false);

  // Storage keys for chat persistence
  const CHAT_STORAGE_KEY = 'yovo_chatbot_messages';
  const CONVERSATION_STORAGE_KEY = 'yovo_chatbot_conversation';
  const CHAT_TIMESTAMP_KEY = 'yovo_chatbot_last_interaction';
  const CHAT_TTL_MS = 2 * 60 * 60 * 1000; // 2 hours

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Trigger opening animation when chatbot opens
  // useEffect(() => {
  //   if (open && !animationComplete) {
  //     setShowOpeningAnimation(true);
  //   } else if (!open) {
  //     setShowOpeningAnimation(false);
  //     setAnimationComplete(false);
  //   }
  // }, [open, animationComplete]);

  // Handle animation completion
  // const handleAnimationComplete = useCallback(() => {
  //   setAnimationComplete(true);
  //   // Delay hiding the opening animation to allow smooth transition
  //   setTimeout(() => {
  //     setShowOpeningAnimation(false);
  //   }, 200);
  //   // Scroll to bottom when chat window appears
  //   setTimeout(() => {
  //     scrollToBottom();
  //   }, 300);
  // }, [scrollToBottom]);

  // Memoize message rendering for better performance
  const renderedMessages = useMemo(() => {
    return messages.map((msg, idx) => (
      <Box key={idx} sx={{ mb: 1.5, display: 'flex', justifyContent: msg.from === 'user' ? 'flex-end' : 'flex-start' }}>
        <Paper sx={{ 
          p: 1.2, 
          bgcolor:msg.from === 'user' ? '#ab2645' : 'white', 
          color: msg.from == 'user' ? 'white':'#0D0D0D', 
          borderRadius: msg.from === 'user' ? '18px 4px 18px 18px' : '18px 18px 18px 4px',
          maxWidth: '85%',
          position: 'relative',
          boxShadow: '0 1px 4px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          }
        }}>
          <Typography variant="body2" sx={{ 
            whiteSpace: 'pre-line', 
            lineHeight: 1.4,
            fontFamily: '"Red Hat Display", -apple-system, Roboto, Helvetica, sans-serif',
            fontSize: '14px',
            fontWeight: 400,
            color: msg.from == 'user' ? 'white':'#0D0D0D', 
            textAlign: msg.from === 'user' ? 'left' : 'left',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5
          }}>
            <Box component="span">
              {msg.text.split('\n').map((line, index) => {
              // Hide flag lines completely
              if (line.includes('[FLAG:')) {
                return null;
              }
              
              // DISABLED: Legacy markdown-style train link parsing
              // Structured actions from backend are now the only trusted path
              const trainMarkdownLinkMatch = null; // Disabled for safety
              if (false /* trainMarkdownLinkMatch && msg.from === 'bot' - DISABLED */) {
                return null;
              }
              
              // DISABLED: Legacy markdown-style hotel link parsing
              // Structured actions from backend are now the only trusted path
              const hotelMarkdownLinkMatch = null; // Disabled for safety
              if (false /* hotelMarkdownLinkMatch && msg.from === 'bot' - DISABLED */) {
                return null;
              }
              
              // DISABLED: Legacy markdown-style flight link parsing
              // Structured actions from backend are now the only trusted path
              const flightMarkdownLinkMatch = null; // Disabled for safety
              if (false /* flightMarkdownLinkMatch && msg.from === 'bot' - DISABLED */) {
                return null;
              }
              
              // DISABLED: Legacy regex-based flight button parsing
              // Structured actions from backend are now the only trusted path
              // Book Flight buttons are rendered via msg.actions from backend
              const bookFlightMatch = null; // Disabled: line.match(/\[Book Flight to ([^\]]+)\]/)
              const bookMulticityMatch = null; // Disabled: line.match(/\[Book Multicity Flight\]/)
              
              if (false /* bookFlightMatch - DISABLED for safety */) {
                return null;
              }
              
              // DISABLED: Legacy multicity flight button - use structured actions instead
              if (false /* bookMulticityMatch - DISABLED for safety */) {
                return null;
              }
              
              // DISABLED: Legacy regex-based train URL parsing
              // Structured actions from backend are now the only trusted path
              // Train buttons are rendered via msg.actions from backend
              let trainUrl = null; // Disabled for safety
              
              if (false /* trainUrl && msg.from === 'bot' - DISABLED for safety */) {
                return null;
              }
              
              // DISABLED: Legacy regex-based hotel URL parsing
              // Structured actions from backend are now the only trusted path
              // Hotel buttons are rendered via msg.actions from backend
              let hotelUrl = null; // Disabled for safety
              
              if (false /* hotelUrl && msg.from === 'bot' - DISABLED for safety */) {
                return null;
              }
              
              // DISABLED: Legacy regex-based standalone flight URL parsing
              // Structured actions from backend are now the only trusted path
              // Flight buttons are rendered via msg.actions from backend
              const flightUrlMatch = null; // Disabled for safety
              if (false /* flightUrlMatch && msg.from === 'bot' - DISABLED for safety */) {
                return null;
              }
              
              // Check for lines that contain only the flight URL (to hide them)
              // This handles both simple URLs and complex URLs with userLocation parameter
              // Note: This is now a fallback since we create buttons above
              if (line.trim().match(/^\/?flight\?[^\s]*$/) && msg.from === 'bot') {
                // This case is already handled above, but if somehow we get here, hide it
                return null;
              }

              // Also hide lines that contain flight URLs with userLocation parameter (multiline URLs)
              if (line.trim().match(/^\/?flight\?.*userLocation.*$/)) {
                return null; // Hide the URL line completely
              }

              // Hide lines that start with flight? and contain complex parameters
              if (line.trim().match(/^\/?flight\?.*&.*$/)) {
                return null; // Hide the URL line completely
              }
              
              // Hide lines that contain only train URLs (to hide them) - this is already handled above by creating a button
              // Check for train URL patterns that should be hidden (already converted to buttons above)
              if (msg.from === 'bot') {
                const trainUrlPattern1 = line.trim().match(/^\/?train\?[^\s]*$/);
                const trainUrlPattern2 = line.trim().match(/^from=[^&\s]+&to=[^&\s]+.*journeyDate/);
                const trainUrlPattern3 = line.trim().includes('from=') && 
                                        line.trim().includes('to=') && 
                                        line.trim().includes('journeyDate') &&
                                        !line.trim().includes('[') && // Not a markdown link
                                        !line.trim().includes(']('); // Not a markdown link
                if (trainUrlPattern1 || trainUrlPattern2 || trainUrlPattern3) {
                  // This case is already handled above by creating a button, so hide the raw URL
                  return null;
                }
                
                // Hide hotel URL patterns (already converted to buttons above)
                const hotelUrlPattern1 = line.trim().match(/^\/?hotel\?[^\s]*$/);
                const hotelUrlPattern2 = line.trim().includes('location=') && 
                                        line.trim().includes('checkIn=') && 
                                        line.trim().includes('checkOut=') &&
                                        !line.trim().includes('[') && // Not a markdown link
                                        !line.trim().includes(']('); // Not a markdown link
                if (hotelUrlPattern1 || hotelUrlPattern2) {
                  // This case is already handled above by creating a button, so hide the raw URL
                  return null;
                }
              }
              
              // Format bullet points and numbered lists
              if (line.trim().startsWith('•') || line.trim().startsWith('-') || line.trim().startsWith('*')) {
                return (
                  <Box key={index} sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    mb: 1,
                    pl: 1
                  }}>
                    <Typography variant="body2" sx={{ 
                      color: '#ab2645', 
                      fontWeight: 600, 
                      mr: 1,
                      fontSize: '16px',
                      lineHeight: 1.2
                    }}>
                      •
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      flex: 1,
                      fontSize: '14px',
                      lineHeight: 1.4,
                      color: '#0D0D0D'
                    }}>
                      {line.trim().substring(1).trim()}
                    </Typography>
                  </Box>
                );
              }
              
              // Format numbered lists
              if (line.trim().match(/^\d+\./)) {
                const number = line.trim().match(/^(\d+)\./)[1];
                const text = line.trim().substring(number.length + 1).trim();
                return (
                  <Box key={index} sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    mb: 1,
                    pl: 1
                  }}>
                    <Typography variant="body2" sx={{ 
                      color: '#ab2645', 
                      fontWeight: 600, 
                      mr: 1,
                      fontSize: '14px',
                      lineHeight: 1.2,
                      minWidth: '20px'
                    }}>
                      {number}.
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      flex: 1,
                      fontSize: '14px',
                      lineHeight: 1.4,
                      color: '#0D0D0D'
                    }}>
                      {text}
                    </Typography>
                  </Box>
                );
              }
              // Regular text
              return line.trim() ? (
                <Typography key={index} variant="body2" sx={{ mb: line.trim() ? 1 : 0 }}>
                  {line}
                </Typography>
              ) : null;
            })}
            </Box>
            {msg.isStreaming && (
              <Box sx={{ 
                display: 'inline-block',
                width: '8px',
                height: '8px',
                bgcolor: '#ab2645',
                borderRadius: '50%',
                animation: 'pulse 1.5s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%': { opacity: 0.4 },
                  '50%': { opacity: 1 },
                  '100%': { opacity: 0.4 }
                }
              }} />
            )}
          </Typography>
          {/* Yovo Guide badge for internal data */}
          {msg.source === 'yovo_guide' && msg.from === 'bot' && (
            <YovoGuideBadge />
          )}
          
          {/* Trip Brief Card */}
          {msg.tripBrief && msg.from === 'bot' && (
            <TripBriefCard brief={msg.tripBrief} />
          )}
          
          {/* Render structured action buttons from backend */}
          {msg.actions && msg.actions.length > 0 && (
            <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {msg.actions.map((action, actionIdx) => (
                <Button
                  key={actionIdx}
                  variant="contained"
                  size="small"
                  startIcon={
                    action.type === 'flight_search' ? <FlightIcon sx={{ fontSize: '14px' }} /> :
                    action.type === 'hotel_search' ? <HotelIcon sx={{ fontSize: '14px' }} /> :
                    action.type === 'train_search' ? <TrainIcon sx={{ fontSize: '14px' }} /> : null
                  }
                  onClick={() => {
                    console.log(`Navigating to ${action.type}:`, action.url);
                    window.location.href = action.url;
                  }}
                  sx={{
                    bgcolor: '#ab2645',
                    color: '#fff',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '6px',
                    px: 2,
                    py: 0.75,
                    '&:hover': { bgcolor: '#8a1f38' }
                  }}
                >
                  {action.label}
                </Button>
              ))}
            </Box>
          )}
          
          {/* Refinement buttons */}
          {msg.refinements && msg.refinements.length > 0 && msg.from === 'bot' && (
            <RefinementButtons 
              refinements={msg.refinements} 
              onSelect={(ref) => {
                // Send refinement request as a new message using the ref
                if (handleSendRef.current) {
                  handleSendRef.current(ref.label);
                }
              }}
            />
          )}
          
          {/* Suggested action chips (from intent classification) */}
          {msg.suggestedActions && msg.suggestedActions.length > 0 && msg.from === 'bot' && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1.5 }}>
              {msg.suggestedActions.map((action) => (
                <Chip
                  key={action.id}
                  label={action.label}
                  size="small"
                  onClick={() => {
                    // Send the action's prompt as a new message
                    if (handleSendRef.current && action.prompt) {
                      handleSendRef.current(action.prompt);
                    }
                  }}
                  sx={{
                    bgcolor: '#f5f5f5',
                    color: '#ab2645',
                    fontSize: '0.7rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    border: '1px solid #ab2645',
                    '&:hover': {
                      bgcolor: 'rgba(171, 38, 69, 0.1)',
                    }
                  }}
                />
              ))}
            </Box>
          )}
        </Paper>
      </Box>
    ));
  }, [messages, navigate]);

  // Check for interactive form flags in messages
  const checkForInteractiveFlags = (messageText) => {
    if (messageText.includes('[FLAG:TRIP_TYPE_SELECTION]')) {
      setInteractiveFormType('trip_type');
      setShowInteractiveForm(true);
      return true;
    }
    if (messageText.includes('[FLAG:DATE_SELECTION]')) {
      setInteractiveFormType('date');
      setShowInteractiveForm(true);
      return true;
    }
    if (messageText.includes('[FLAG:DEPARTURE_CITY_SELECTION]')) {
      setInteractiveFormType('departure_city');
      setShowInteractiveForm(true);
      return true;
    }
    if (messageText.includes('[FLAG:TRIP_AND_DATES_ONLY]')) {
      setInteractiveFormType('trip_and_dates');
      setShowInteractiveForm(true);
      return true;
    }
    if (messageText.includes('[FLAG:ALL_FLIGHT_DETAILS]')) {
      setInteractiveFormType('all');
      setShowInteractiveForm(true);
      return true;
    }
    return false;
  };

  // Load departure city options from real API
  const loadDepartureCityOptions = async (query) => {
    if (!query || query.length < 2) return;
    
    setDepartureCityLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/airports/search?q=${encodeURIComponent(query)}&limit=10`);
      const data = await response.json();
      
      if (data.success && data.data) {
        const airports = data.data.map(airport => ({
          code: airport.iataCode,
          name: `${airport.address?.cityName || airport.name} (${airport.iataCode})`,
          city: airport.address?.cityName || '',
          country: airport.address?.countryName || ''
        }));
        setDepartureCityOptions(airports);
      } else {
        setDepartureCityOptions([]);
      }
    } catch (error) {
      console.error('Error loading departure cities:', error);
      setDepartureCityOptions([]);
    } finally {
      setDepartureCityLoading(false);
    }
  };

  // Auto-send when all required fields are filled
  const checkAndAutoSend = useCallback(() => {
    if (interactiveFormType === 'all') {
      if (departureCity && departureDate && tripType) {
        // For round-trip, wait for both dates to be selected
        if (tripType === 'roundtrip' && !returnDate) {
          return; // Don't auto-send yet, wait for return date
        }
        
        const message = `I want to book a ${tripType} flight from ${departureCity.name} departing on ${departureDate.toLocaleDateString()}${tripType === 'roundtrip' && returnDate ? ` and returning on ${returnDate.toLocaleDateString()}` : ''}`;
        handleSend(message);
        setShowInteractiveForm(false);
        setInteractiveFormType('');
        setDepartureCity(null);
        setDepartureDate(null);
        setReturnDate(null);
        setShowDatePicker(false); // Close the date picker popup
      }
    } else if (interactiveFormType === 'trip_and_dates') {
      if (departureDate && tripType) {
        // For round-trip, wait for both dates to be selected
        if (tripType === 'roundtrip' && !returnDate) {
          return; // Don't auto-send yet, wait for return date
        }
        
        const message = `I want to book a ${tripType} flight departing on ${departureDate.toLocaleDateString()}${tripType === 'roundtrip' && returnDate ? ` and returning on ${returnDate.toLocaleDateString()}` : ''}`;
        handleSend(message);
        setShowInteractiveForm(false);
        setInteractiveFormType('');
        setDepartureDate(null);
        setReturnDate(null);
        setShowDatePicker(false); // Close the date picker popup
      }
    }
  }, [interactiveFormType, departureCity, departureDate, returnDate, tripType]);

  useEffect(() => {
    checkAndAutoSend();
  }, [checkAndAutoSend]);

  // Load chat history from localStorage on component mount (with TTL check)
  useEffect(() => {
    try {
      // Check TTL - clear stale history
      const lastInteraction = localStorage.getItem(CHAT_TIMESTAMP_KEY);
      const isStale = lastInteraction && (Date.now() - parseInt(lastInteraction, 10) > CHAT_TTL_MS);
      
      if (isStale) {
        localStorage.removeItem(CHAT_STORAGE_KEY);
        localStorage.removeItem(CONVERSATION_STORAGE_KEY);
        localStorage.removeItem(CHAT_TIMESTAMP_KEY);
        // Also clear floating chatbot storage for consistency
        localStorage.removeItem('yovo_floating_chatbot_messages');
        localStorage.removeItem('yovo_floating_chatbot_conversation');
      }
      
      const savedMessages = localStorage.getItem(CHAT_STORAGE_KEY);
      const savedConversation = localStorage.getItem(CONVERSATION_STORAGE_KEY);
      
      if (savedMessages && !isStale) {
        const parsedMessages = JSON.parse(savedMessages);
        setMessages(parsedMessages);
      }
      
      if (savedConversation && !isStale) {
        const parsedConversation = JSON.parse(savedConversation);
        setConversationHistory(parsedConversation);
      }
      
      // Check if there's an initial message from homepage (regardless of saved messages)
      if (window.chatbotInitialMessage) {
        setTimeout(() => {
          handleSend(window.chatbotInitialMessage);
          delete window.chatbotInitialMessage;
        }, 1000);
      }
      
      // If no saved messages or stale, show welcome message
      if (!savedMessages || isStale || JSON.parse(savedMessages).length === 0) {
        const welcomeMessage = {
          from: 'bot',
          text: isStale ? "Starting a fresh conversation. How can I help with your travel plans today?" : fallbackResponses.greeting,
          timestamp: new Date()
        };
        setMessages([welcomeMessage]);
      }
    } catch (error) {
      console.error('Error loading chat history:', error);
      const welcomeMessage = {
        from: 'bot',
        text: fallbackResponses.greeting,
        timestamp: new Date()
      };
      setMessages([welcomeMessage]);
    }
  }, []); // Only run once on mount

  // Save messages to localStorage whenever messages change
  useEffect(() => {
    if (messages.length > 0) {
      try {
        localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(messages));
        localStorage.setItem(CHAT_TIMESTAMP_KEY, Date.now().toString());
      } catch (error) {
        console.error('Error saving messages to localStorage:', error);
      }
    }
  }, [messages]);

  // Save conversation history to localStorage whenever it changes
  useEffect(() => {
    if (conversationHistory.length > 0) {
      try {
        localStorage.setItem(CONVERSATION_STORAGE_KEY, JSON.stringify(conversationHistory));
        localStorage.setItem(CHAT_TIMESTAMP_KEY, Date.now().toString());
      } catch (error) {
        console.error('Error saving conversation history to localStorage:', error);
      }
    }
  }, [conversationHistory]);

  // No longer needed - welcome message is handled in the load effect


  const handleSend = async (messageText = input) => {
    if (!messageText.trim()) return;

    const userMessage = {
      from: 'user',
      text: messageText,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(false); // We'll handle loading state with streaming

    // Create initial bot message for streaming
    const botMessage = {
      from: 'bot',
      text: '',
      timestamp: new Date(),
      error: null,
      isStreaming: true
    };

    setMessages(prev => [...prev, botMessage]);

    try {
      // Prepare conversation history for context
      const historyForApi = conversationHistory.slice(-10); // Keep last 10 messages for context
      
      let fullResponse = '';
      
      const response = await chatbotService.sendMessageStream(
        messageText, 
        historyForApi,
        (chunk) => {
          // Update the bot message with new chunk
          fullResponse += chunk;
          setMessages(prev => {
            const newMessages = [...prev];
            const lastMessage = newMessages[newMessages.length - 1];
            if (lastMessage.from === 'bot' && lastMessage.isStreaming) {
              lastMessage.text = fullResponse;
            }
            return newMessages;
          });
        },
        offTopicCount
      );
      
      // Update off-topic counter from response
      if (typeof response.offTopicCount === 'number') {
        setOffTopicCount(response.offTopicCount);
      }
      
      // Mark streaming as complete and add actions, tripBrief, refinements, suggestedActions
      setMessages(prev => {
        const newMessages = [...prev];
        const lastMessage = newMessages[newMessages.length - 1];
        if (lastMessage.from === 'bot' && lastMessage.isStreaming) {
          lastMessage.isStreaming = false;
          lastMessage.text = response.success ? response.message : response.message;
          lastMessage.error = !response.success ? response.error : null;
          lastMessage.actions = response.actions || [];
          lastMessage.suggestedActions = response.suggestedActions || null;
          lastMessage.tripBrief = response.tripBrief || null;
          lastMessage.refinements = response.refinements || null;
          lastMessage.source = response.source || null;
        }
        return newMessages;
      });
      
      // Update current trip brief if present
      if (response.tripBrief) {
        setCurrentTripBrief(response.tripBrief);
      }
      
      // Check for interactive form flags
      if (response.success) {
        checkForInteractiveFlags(response.message);
      }
      
      // Update conversation history
      setConversationHistory(prev => [
        ...prev,
        { role: 'user', content: messageText },
        { role: 'assistant', content: response.success ? response.message : response.message }
      ]);

    } catch (error) {
      console.error('Error sending message:', error);
      
      // Update the streaming message to show error
      setMessages(prev => {
        const newMessages = [...prev];
        const lastMessage = newMessages[newMessages.length - 1];
        if (lastMessage.from === 'bot' && lastMessage.isStreaming) {
          lastMessage.isStreaming = false;
          lastMessage.text = fallbackResponses.error;
          lastMessage.error = true;
        }
        return newMessages;
      });
    }
  };
  
  // Store handleSend in ref for child components (refinement buttons)
  handleSendRef.current = handleSend;
  
  // Handle plan style selection
  const handlePlanStyleSelect = (style) => {
    setSelectedPlanStyle(style);
    handleSend(`I prefer a ${style.label.toLowerCase()} trip`);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Parse travel guide responses and extract flight booking information
  const parseTravelGuideResponse = (userMessage, aiResponse) => {
    const destinations = [];
    let departureCity = '';
    let travelDate = '';
    let destination = '';
    let showFlightBooking = false;

    // Check if this is a flight booking request
    const bookingPatterns = [
      /book.*flight/i,
      /yes.*book/i,
      /book.*ticket/i,
      /find.*flight/i,
      /search.*flight/i
    ];

    const isBookingRequest = bookingPatterns.some(pattern => 
      pattern.test(userMessage) || pattern.test(aiResponse)
    );

    // Extract departure city from various patterns
    const cityPatterns = [
      /from\s+([a-zA-Z\s]+?)(?:\s+on|\s+to|\s*$)/i,
      /flying\s+from\s+([a-zA-Z\s]+)/i,
      /departure\s+(?:city|from)\s*:?\s*([a-zA-Z\s]+)/i,
      /leaving\s+from\s+([a-zA-Z\s]+)/i,
      /starting\s+from\s+([a-zA-Z\s]+)/i
    ];

    // Check current message for departure city
    for (const pattern of cityPatterns) {
      const cityMatch = userMessage.match(pattern);
      if (cityMatch) {
        departureCity = cityMatch[1].trim();
        break;
      }
    }

    // Check conversation history for departure city
    if (!departureCity) {
      const recentMessages = conversationHistory.slice(-5);
      for (let i = recentMessages.length - 1; i >= 0; i--) {
        const message = recentMessages[i].content;
        for (const pattern of cityPatterns) {
          const cityMatch = message.match(pattern);
          if (cityMatch) {
            departureCity = cityMatch[1].trim();
            break;
          }
        }
        if (departureCity) break;
      }
    }

    // Extract travel dates
    const datePatterns = [
      /on\s+(\d{1,2}(?:st|nd|rd|th)?\s+(?:january|february|march|april|may|june|july|august|september|october|november|december))/i,
      /on\s+(\d{1,2}\/\d{1,2}\/\d{4})/,
      /on\s+(\d{4}-\d{2}-\d{2})/,
      /(\d{1,2}(?:st|nd|rd|th)?\s+(?:january|february|march|april|may|june|july|august|september|october|november|december))/i,
      /date\s*:?\s*(\d{1,2}[\/-]\d{1,2}[\/-]\d{4})/i,
      /(tomorrow|today|next week|next month)/i
    ];

    for (const pattern of datePatterns) {
      const dateMatch = userMessage.match(pattern);
      if (dateMatch) {
        travelDate = dateMatch[1];
        break;
      }
    }

    // Check conversation history for dates
    if (!travelDate) {
      const recentMessages = conversationHistory.slice(-5);
      for (let i = recentMessages.length - 1; i >= 0; i--) {
        const message = recentMessages[i].content;
        for (const pattern of datePatterns) {
          const dateMatch = message.match(pattern);
          if (dateMatch) {
            travelDate = dateMatch[1];
            break;
          }
        }
        if (travelDate) break;
      }
    }

    // Extract destination from AI response
    const destinationPatterns = [
      /to\s+([A-Za-z\s]+?)(?:\s+\(|\s+for|\s*$)/i,
      /([A-Za-z\s]+?)\s*\(([A-Z]{3})\)/,
      /destination.*?([A-Za-z\s]+)/i
    ];

    for (const pattern of destinationPatterns) {
      const destMatch = aiResponse.match(pattern);
      if (destMatch) {
        destination = destMatch[1] || destMatch[2];
        break;
      }
    }

    // Check if AI response indicates flight booking should be shown
    const bookingIndicators = [
      /book.*flight/i,
      /flight.*booking/i,
      /find.*flight/i,
      /search.*flight/i,
      /Book Flight to/i
    ];

    showFlightBooking = bookingIndicators.some(pattern => pattern.test(aiResponse));

    // If we have a destination and booking is requested, create destination info
    if (destination && (isBookingRequest || showFlightBooking)) {
      // Map common destinations to airport codes
      const destinationAirports = {
        'thailand': { code: 'BKK', city: 'Bangkok' },
        'bangkok': { code: 'BKK', city: 'Bangkok' },
        'phuket': { code: 'HKT', city: 'Phuket' },
        'singapore': { code: 'SIN', city: 'Singapore' },
        'dubai': { code: 'DXB', city: 'Dubai' },
        'paris': { code: 'CDG', city: 'Paris' },
        'london': { code: 'LHR', city: 'London' },
        'tokyo': { code: 'NRT', city: 'Tokyo' },
        'new york': { code: 'JFK', city: 'New York' },
        'bali': { code: 'DPS', city: 'Bali' },
        'goa': { code: 'GOI', city: 'Goa' },
        'mumbai': { code: 'BOM', city: 'Mumbai' },
        'delhi': { code: 'DEL', city: 'Delhi' }
      };

      const destKey = destination.toLowerCase();
      const airportInfo = destinationAirports[destKey] || { code: 'XXX', city: destination };
        
        destinations.push({
        city: airportInfo.city,
        code: airportInfo.code,
        label: `${airportInfo.city} (${airportInfo.code})`,
        description: `Amazing destination with rich culture and experiences`
      });
    }

    return {
      departureCity,
      travelDate,
      destinations,
      destination,
      showFlightBooking
    };
  };

  // DISABLED: Legacy handleFlightSearch with hardcoded cityToCode
  // All flight searches now go through structured actions from backend
  // eslint-disable-next-line no-unused-vars
  const handleFlightSearch = (fromCity, toCode, date) => {
    // This function is disabled - all flight navigation should use structured actions
    console.warn('handleFlightSearch is disabled. Use structured actions from backend.');
    // Do not navigate - this prevents any accidental use of hardcoded codes
  };

  // Clear chat history (including sibling widgets for consistency)
  const clearChatHistory = () => {
    try {
      localStorage.removeItem(CHAT_STORAGE_KEY);
      localStorage.removeItem(CONVERSATION_STORAGE_KEY);
      localStorage.removeItem(CHAT_TIMESTAMP_KEY);
      // Also clear floating chatbot storage for consistency
      localStorage.removeItem('yovo_floating_chatbot_messages');
      localStorage.removeItem('yovo_floating_chatbot_conversation');
      // Clear chatbot session ID to reset quotas
      clearChatbotSession();
      
      // Reset to welcome message
      const welcomeMessage = {
        from: 'bot',
        text: fallbackResponses.greeting,
        timestamp: new Date()
      };
      
      setMessages([welcomeMessage]);
      setConversationHistory([]);
      
    } catch (error) {
      console.error('Error clearing chat history:', error);
    }
  };

  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      backgroundColor: 'var(--Neutrals-grey-200, #E8E9ED)',
      position: 'relative',
      fontFamily: '"Red Hat Display", -apple-system, Roboto, Helvetica, sans-serif',
      borderRadius: flushBottom ? '16px 16px 0 0' : '16px',
      overflow: 'hidden',
      // Epic entrance animation sequence - starts earlier for smoother transition
      // animation: animationComplete ? 'chatbotEpicEntrance 1.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.2s both' : 'none',
      '@keyframes chatbotEpicEntrance': {
        '0%': {
          transform: 'translateY(120px) scale(0.4) rotateX(30deg)',
          opacity: 0,
          filter: 'blur(8px)',
        },
        '15%': {
          transform: 'translateY(60px) scale(0.7) rotateX(15deg)',
          opacity: 0.2,
          filter: 'blur(4px)',
        },
        '30%': {
          transform: 'translateY(20px) scale(0.9) rotateX(5deg)',
          opacity: 0.5,
          filter: 'blur(2px)',
        },
        '50%': {
          transform: 'translateY(-10px) scale(1.02) rotateX(-2deg)',
          opacity: 0.8,
          filter: 'blur(1px)',
        },
        '70%': {
          transform: 'translateY(5px) scale(0.99) rotateX(1deg)',
          opacity: 0.95,
          filter: 'blur(0px)',
        },
        '85%': {
          transform: 'translateY(-2px) scale(1.005) rotateX(-0.5deg)',
          opacity: 0.98,
        },
        '100%': {
          transform: 'translateY(0) scale(1) rotateX(0deg)',
          opacity: 1,
        }
      }
    }}>
      {/* Opening Animation Overlay */}
      {/* <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1000,
          opacity: showOpeningAnimation ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out',
          pointerEvents: showOpeningAnimation ? 'none' : 'none',
        }}
      >
        <AnimatedPlane 
          key={showOpeningAnimation ? 'animation-active' : 'animation-inactive'}
          isVisible={showOpeningAnimation} 
          onComplete={handleAnimationComplete}
        />
      </Box> */}
      
      {/* Chatbot Content - Only show after animation completes */}
      <Box
        sx={{
          // opacity: animationComplete ? 1 : 0,
          // transition: 'opacity 0.5s ease-in-out 0.2s',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* {animationComplete && ( */}
          <>
          {/* Header Section */}
          <Box sx={{
        backgroundColor: '#000000', 
        p: 1.5, 
        flexShrink: 0,
        borderTopLeftRadius: '16px',
        borderTopRightRadius: '16px',
        borderBottom: '1px solid #333',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, position: 'relative', zIndex: 1 }}>
          <Box sx={{
            color: '#fff',
            width: 20,
            height: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 16,
            mr: 1,
            position: 'relative'
          }}>
            <AutoAwesome sx={{ 
              fontSize: 16, 
              color: '#fff'
            }} />
          </Box>
          <Box sx={{ 
            flex: 1
          }}>
            <Typography variant="subtitle1" fontWeight={600} color='white' sx={{ 
              fontSize: '0.85rem'
            }}>
              Yovo Travel Guide
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton 
              onClick={clearChatHistory} 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.7)', 
                '&:hover': { 
                  color: 'white',
                  bgcolor: 'rgba(255, 255, 255, 0.1)' 
                },
                p: 0.5,
                minWidth: 'auto'
              }}
              title="Clear chat history"
            >
              <DeleteIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton 
              onClick={() => setOpen(false)} 
              sx={{ 
                color: 'white',
                '&:hover': { 
                  bgcolor: 'rgba(255, 255, 255, 0.1)' 
                },
                p: 0.5,
                minWidth: 'auto'
              }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Box>
      </Box>
      
      {/* Messages Area - Compact Design with Travel Animation */}
      <Box sx={{ 
        flex: 1,
        overflowY: 'auto', 
        px: 2,
        py: 1.5,
        minHeight: 0,
        backgroundColor: '#F4F4F4',
        position: 'relative',
        // Subtle background pattern
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 80%, rgba(171, 38, 69, 0.02) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(0, 0, 0, 0.02) 0%, transparent 50%)',
          pointerEvents: 'none',
          animation: 'backgroundFloat 8s ease-in-out infinite',
          '@keyframes backgroundFloat': {
            '0%, 100%': { opacity: 0.3 },
            '50%': { opacity: 0.6 }
          }
        },
        // Custom scrollbar styling
        '&::-webkit-scrollbar': {
          width: '4px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(0, 0, 0, 0.2)',
          borderRadius: '2px',
        },
        // Firefox scrollbar
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(0, 0, 0, 0.2) transparent',
      }}>
        {/* Current Trip Brief - sticky at top */}
        {currentTripBrief && (
          <Box sx={{ mb: 1.5, position: 'sticky', top: 0, zIndex: 10, px: 0.5 }}>
            <TripBriefCard 
              brief={currentTripBrief} 
              onClose={() => setCurrentTripBrief(null)} 
            />
          </Box>
        )}
        
        {renderedMessages}
          
          {/* Loading indicator */}
          {isLoading && (
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-start' }}>
              <Paper sx={{ 
                p: 1.5, 
                bgcolor: 'rgba(35, 37, 38, 0.9)', 
                color: 'white', 
                borderRadius: '18px 18px 18px 4px',
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                animation: 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%': { opacity: 0.8 },
                  '50%': { opacity: 1 },
                  '100%': { opacity: 0.8 },
                }
              }}>
                <CircularProgress size={16} sx={{ color: '#7B61FF' }} />
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                  Yovo is typing...
                </Typography>
              </Paper>
            </Box>
          )}
          
          <div ref={messagesEndRef} />
        </Box>
      
      {/* Interactive Form Area */}
      {showInteractiveForm && (
      <Box sx={{ 
        p: 2, 
        bgcolor: '#F8F9FA',
        borderTop: '1px solid #E0E0E0',
        borderBottom: '1px solid #E0E0E0',
        position: 'relative'
      }}>
        {/* Close Button for Interactive Form */}
        <IconButton 
          onClick={() => {
            setShowInteractiveForm(false);
            setInteractiveFormType('');
            setDepartureCity(null);
            setDepartureDate(null);
            setReturnDate(null);
            setShowDatePicker(false);
          }}
          sx={{ 
            position: 'absolute',
            top: 8,
            right: 8,
            color: '#666',
            '&:hover': { 
              color: '#ab2645',
              bgcolor: 'rgba(171, 38, 69, 0.1)' 
            },
            p: 0.5,
            minWidth: 'auto',
            zIndex: 1
          }}
          size="small"
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
          {interactiveFormType === 'trip_type' && (
            <FormControl component="fieldset" sx={{ width: '100%' }}>
              <FormLabel component="legend" sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#333', mb: 1 }}>
                Select Trip Type
              </FormLabel>
              <RadioGroup
                value={tripType}
                onChange={(e) => setTripType(e.target.value)}
                row
                sx={{ gap: 2 }}
              >
                <FormControlLabel 
                  value="oneway" 
                  control={<Radio size="small" />} 
                  label="One-way" 
                  sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.85rem' } }}
                />
                <FormControlLabel 
                  value="roundtrip" 
                  control={<Radio size="small" />} 
                  label="Round-trip" 
                  sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.85rem' } }}
                />
              </RadioGroup>
            </FormControl>
          )}

          {interactiveFormType === 'date' && (
            <Box sx={{ width: '100%' }}>
              <Typography variant="subtitle2" sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#333', mb: 1 }}>
                Select Travel Dates
              </Typography>
              <Button
                variant="outlined"
                startIcon={<CalendarIcon />}
                onClick={() => setShowDatePicker(true)}
                fullWidth
                sx={{
                  textTransform: 'none',
                  fontSize: '0.8rem',
                  borderColor: '#E0E0E0',
                  color: '#666',
                  py: 1.5,
                  '&:hover': { borderColor: '#ab2645', color: '#ab2645' }
                }}
              >
                {tripType === 'oneway' 
                  ? (departureDate ? departureDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Select departure date')
                  : (departureDate && returnDate 
                      ? `${departureDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${returnDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                      : departureDate 
                        ? `${departureDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - Select return date`
                        : 'Select travel dates'
                    )
                }
              </Button>
            </Box>
          )}

          {interactiveFormType === 'departure_city' && (
            <Box sx={{ width: '100%' }}>
              <Typography variant="subtitle2" sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#333', mb: 1 }}>
                Select Departure City
              </Typography>
              <Autocomplete
                freeSolo
                loading={departureCityLoading}
                options={departureCityOptions}
                value={departureCity}
                onChange={(event, newValue) => setDepartureCity(newValue)}
                onInputChange={(event, newInputValue) => {
                  if (newInputValue) {
                    loadDepartureCityOptions(newInputValue);
                  }
                }}
                getOptionLabel={(option) => option.name || option}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search departure city..."
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '0.8rem',
                        '& fieldset': { borderColor: '#E0E0E0' },
                        '&:hover fieldset': { borderColor: '#ab2645' },
                        '&.Mui-focused fieldset': { borderColor: '#ab2645' }
                      }
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {option.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.city}, {option.country}
                      </Typography>
                    </Box>
                  </Box>
                )}
              />
            </Box>
          )}

          {interactiveFormType === 'trip_and_dates' && (
            <Box sx={{ width: '100%' }}>
              <Typography variant="subtitle2" sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#333', mb: 2 }}>
                Complete Your Flight Details
              </Typography>
              
              {/* Trip Type */}
              <FormControl component="fieldset" sx={{ width: '100%', mb: 2 }}>
                <FormLabel component="legend" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#666', mb: 1 }}>
                  Trip Type
                </FormLabel>
                <RadioGroup
                  value={tripType}
                  onChange={(e) => setTripType(e.target.value)}
                  row
                  sx={{ gap: 2 }}
                >
                  <FormControlLabel 
                    value="oneway" 
                    control={<Radio size="small" />} 
                    label="One-way" 
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                  />
                  <FormControlLabel 
                    value="roundtrip" 
                    control={<Radio size="small" />} 
                    label="Round-trip" 
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                  />
                </RadioGroup>
              </FormControl>

              {/* Dates */}
              <Box sx={{ width: '100%' }}>
                <Typography variant="subtitle2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#666', mb: 1 }}>
                  Travel Dates
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<CalendarIcon />}
                  onClick={() => setShowDatePicker(true)}
                  fullWidth
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.8rem',
                    borderColor: '#E0E0E0',
                    color: '#666',
                    py: 1.5,
                    '&:hover': { borderColor: '#ab2645', color: '#ab2645' }
                  }}
                >
                  {tripType === 'oneway' 
                    ? (departureDate ? departureDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Select departure date')
                    : (departureDate && returnDate 
                        ? `${departureDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${returnDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                        : departureDate 
                          ? `${departureDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - Select return date`
                          : 'Select travel dates'
                      )
                  }
                </Button>
              </Box>
            </Box>
          )}

          {interactiveFormType === 'all' && (
            <Box sx={{ width: '100%' }}>
              <Typography variant="subtitle2" sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#333', mb: 2 }}>
                Complete Your Flight Details
              </Typography>
              
              {/* Trip Type */}
              <FormControl component="fieldset" sx={{ width: '100%', mb: 2 }}>
                <FormLabel component="legend" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#666', mb: 1 }}>
                  Trip Type
                </FormLabel>
                <RadioGroup
                  value={tripType}
                  onChange={(e) => setTripType(e.target.value)}
                  row
                  sx={{ gap: 2 }}
                >
                  <FormControlLabel 
                    value="oneway" 
                    control={<Radio size="small" />} 
                    label="One-way" 
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                  />
                  <FormControlLabel 
                    value="roundtrip" 
                    control={<Radio size="small" />} 
                    label="Round-trip" 
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                  />
                </RadioGroup>
              </FormControl>

              {/* Departure City */}
              <Box sx={{ width: '100%', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#666', mb: 1 }}>
                  Departure City
                </Typography>
                <Autocomplete
                  freeSolo
                  loading={departureCityLoading}
                  options={departureCityOptions}
                  value={departureCity}
                  onChange={(event, newValue) => setDepartureCity(newValue)}
                  onInputChange={(event, newInputValue) => {
                    if (newInputValue) {
                      loadDepartureCityOptions(newInputValue);
                    }
                  }}
                  getOptionLabel={(option) => option.name || option}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search departure city..."
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          fontSize: '0.8rem',
                          '& fieldset': { borderColor: '#E0E0E0' },
                          '&:hover fieldset': { borderColor: '#ab2645' },
                          '&.Mui-focused fieldset': { borderColor: '#ab2645' }
                        }
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {option.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.city}, {option.country}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                />
              </Box>

              {/* Dates */}
              <Box sx={{ width: '100%' }}>
                <Typography variant="subtitle2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#666', mb: 1 }}>
                  Travel Dates
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<CalendarIcon />}
                  onClick={() => setShowDatePicker(true)}
                  fullWidth
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.8rem',
                    borderColor: '#E0E0E0',
                    color: '#666',
                    py: 1.5,
                    '&:hover': { borderColor: '#ab2645', color: '#ab2645' }
                  }}
                >
                  {tripType === 'oneway' 
                    ? (departureDate ? departureDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Select departure date')
                    : (departureDate && returnDate 
                        ? `${departureDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${returnDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                        : departureDate 
                          ? `${departureDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - Select return date`
                          : 'Select travel dates'
                      )
                  }
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Date Picker Dialog - Compact */}
      <Dialog 
        open={showDatePicker} 
        onClose={() => setShowDatePicker(false)} 
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 2,
            backgroundColor: '#F8F9FA',
            maxWidth: '400px',
            maxHeight: '450px'
          }
        }}
      >
        {/* Dialog Header with Close Button */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          p: 1.5,
          borderBottom: '1px solid #E0E0E0',
          backgroundColor: '#fff'
        }}>
          <Typography variant="h6" sx={{ 
            fontSize: '1rem', 
            fontWeight: 600, 
            color: '#333',
            fontFamily: '"Red Hat Display", -apple-system, Roboto, Helvetica, sans-serif'
          }}>
            Select Travel Dates
          </Typography>
          <IconButton 
            onClick={() => setShowDatePicker(false)}
            sx={{ 
              color: '#666',
              '&:hover': { 
                color: '#ab2645',
                bgcolor: 'rgba(171, 38, 69, 0.1)' 
              },
              p: 0.5,
              minWidth: 'auto'
            }}
            size="small"
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
        
        <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
          <Box sx={{ 
            position: 'relative', 
            width: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <style>{`
              .custom-datepicker {
                border: none !important;
                box-shadow: none !important;
                font-family: 'Host Grotesk', sans-serif !important;
                width: 100% !important;
              }
              
              .custom-datepicker .react-datepicker__header {
                background-color: #000 !important;
                border-bottom: 1px solid #333 !important;
                border-radius: 8px 8px 0 0 !important;
                padding: 8px 12px !important;
              }
              
              .custom-datepicker .react-datepicker__current-month {
                color: #fff !important;
                font-weight: 600 !important;
                font-size: 14px !important;
              }
              
              .custom-datepicker .react-datepicker__day-name {
                color: #666 !important;
                font-weight: 500 !important;
                font-size: 12px !important;
                width: calc(100% / 7 - 4px) !important;
                flex: 1 !important;
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
                margin: 2px !important;
                padding: 4px 0 !important;
              }
              
              .custom-datepicker .react-datepicker__day {
                color: #333 !important;
                font-weight: 400 !important;
                border-radius: 4px !important;
                margin: 1px !important;
                width: calc(100% / 7 - 2px) !important;
                height: 100% !important;
                line-height: 1 !important;
                flex: 1 !important;
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
                font-size: 13px !important;
                aspect-ratio: 1 !important;
              }
              
              .custom-datepicker .react-datepicker__day:hover {
                background-color: #f0f0f0 !important;
                color: #000 !important;
              }
              
              .custom-datepicker .react-datepicker__day--selected {
                background-color: #000 !important;
                color: #fff !important;
                font-weight: 600 !important;
              }
              
              .custom-datepicker .react-datepicker__day--in-selecting-range {
                background-color: #f0f0f0 !important;
                color: #000 !important;
              }
              
              .custom-datepicker .react-datepicker__day--in-range {
                background-color: #f0f0f0 !important;
                color: #000 !important;
              }
              
              .custom-datepicker .react-datepicker__day--range-start,
              .custom-datepicker .react-datepicker__day--range-end {
                background-color: #000 !important;
                color: #fff !important;
                font-weight: 600 !important;
              }
              
              .custom-datepicker .react-datepicker__day--disabled {
                color: #ccc !important;
                cursor: not-allowed !important;
              }
              
              .custom-datepicker .react-datepicker__navigation {
                background: none !important;
                border: none !important;
                color: #fff !important;
              }
              
              .custom-datepicker .react-datepicker__navigation--previous {
                left: 8px !important;
              }
              
              .custom-datepicker .react-datepicker__navigation--next {
                right: 8px !important;
              }
              
              .custom-datepicker .react-datepicker__navigation-icon::before {
                border-color: #fff !important;
                border-width: 2px 2px 0 0 !important;
              }
              
              .custom-datepicker .react-datepicker__navigation:hover .react-datepicker__navigation-icon::before {
                border-color: #ccc !important;
              }
              
              .custom-datepicker .react-datepicker__month-container {
                background: #fff !important;
                border-radius: 0 0 8px 8px !important;
                padding: 0 !important;
                width: 100% !important;
                aspect-ratio: 1 !important;
              }
              
              .custom-datepicker .react-datepicker__month {
                margin: 0 !important;
                padding: 0 !important;
                width: 100% !important;
                height: 100% !important;
                display: flex !important;
                flex-direction: column !important;
              }
              
              .custom-datepicker .react-datepicker__week {
                display: flex !important;
                justify-content: space-between !important;
                margin: 0 !important;
                flex: 1 !important;
                height: 100% !important;
              }
              
              .custom-datepicker .react-datepicker__day-names {
                display: flex !important;
                justify-content: space-between !important;
                margin: 0 !important;
                padding: 8px 8px 0 8px !important;
              }
            `}</style>
            
            {tripType === 'oneway' ? (
              <DatePicker
                selected={departureDate}
                onChange={(date) => setDepartureDate(date)}
                dateFormat="MMM dd, yyyy"
                minDate={new Date()}
                inline
                showPopperArrow={false}
                calendarClassName="custom-datepicker"
              />
            ) : (
              <DatePicker
                selected={departureDate}
                onChange={(dates) => {
                  if (dates) {
                    const [start, end] = dates;
                    setDepartureDate(start);
                    setReturnDate(end);
                  }
                }}
                startDate={departureDate}
                endDate={returnDate}
                selectsRange
                dateFormat="MMM dd, yyyy"
                minDate={new Date()}
                inline
                showPopperArrow={false}
                calendarClassName="custom-datepicker"
                shouldCloseOnSelect={false}
              />
            )}
            
            <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              justifyContent: 'flex-end',
              borderTop: '1px solid #f0f0f0',
              pt: 1,
              mt: 0.5,
              px: 1.5,
              pb: 1.5
            }}>
              <Button
                size="small"
                onClick={() => {
                  setDepartureDate(null);
                  setReturnDate(null);
                  setShowDatePicker(false);
                }}
                sx={{ 
                  fontSize: '0.75rem', 
                  textTransform: 'none',
                  color: '#666',
                  fontWeight: 500,
                  px: 1.5,
                  py: 0.25,
                  borderRadius: 1,
                  minWidth: 'auto'
                }}
              >
                Clear
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => setShowDatePicker(false)}
                sx={{ 
                  fontSize: '0.75rem', 
                  textTransform: 'none',
                  background: '#000',
                  fontWeight: 600,
                  px: 1.5,
                  py: 0.25,
                  borderRadius: 1,
                  minWidth: 'auto',
                  '&:hover': { background: '#333' }
                }}
              >
                Done
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Input Area - Compact Design */}
      <Box sx={{ 
        p: 1, 
        display: 'flex', 
        alignItems: 'center',
        flexShrink: 0,
        borderBottomLeftRadius: flushBottom ? 0 : '16px',
        borderBottomRightRadius: flushBottom ? 0 : '16px',
        position: 'relative',
        backgroundColor: '#F4F4F4'
      }}>
        <Box sx={{
          flex: 1,
          display: 'flex',
          padding: '8px 12px',
          alignItems: 'center',
          gap: '8px',
          borderRadius: '20px',
          border: '1px solid #E0E0E0',
          background: 'var(--Neutrals-grey-200, #E8E9ED)',
          position: 'relative',
          minHeight: '40px',
      }}>
        <TextField
          variant="outlined"
          size="small"
            placeholder="Ask me anything travel"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={isLoading}
          sx={{ 
            flex: 1, 
              background: 'transparent',
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: 'transparent' },
              '&:hover fieldset': { borderColor: 'transparent' },
              '&.Mui-focused fieldset': { borderColor: 'transparent' },
              padding: 0,
            },
            '& .MuiInputBase-input': {
                color: '#0D0D0D',
              fontSize: { xs: '16px', sm: '16px', md: '13px' },
                fontWeight: 500,
                fontFamily: '"Red Hat Display", -apple-system, Roboto, Helvetica, sans-serif',
              padding: '8px 0',
              },
          }}
          InputProps={{ 
              style: { color: '#0D0D0D' },
            endAdornment: isLoading ? <CircularProgress size={14} sx={{ color: '#666' }} /> : null
          }}
        />
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => handleSend()}
          disabled={!input.trim() || isLoading}
          sx={{ 
            display: 'flex',
            padding: '6px',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            background: '#000',
            border: 'none',
            cursor: 'pointer',
            width: '32px',
            height: '32px',
            minWidth: 'auto',
            transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            position: 'relative',
            zIndex: 1,
            '&:hover': {
              transform: 'scale(1.1) rotate(5deg)',
              background: '#333',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            },
            '&:active': {
              transform: 'scale(0.95)',
            },
            '&:disabled': {
              opacity: 0.5,
              cursor: 'not-allowed',
              transform: 'none',
              animation: 'none',
            },
            '&:disabled:hover': {
              background: '#000',
              transform: 'none',
            }
          }}
        >
          <SendIcon sx={{ fontSize: 14, fill: 'white' }} />
        </Button>
        </Box>
      </Box>
          </>
        {/* )} */}
      </Box>
    </Box>
  );
});

export default Chatbot; 